// CompetitionLeaderboardCard — generic leaderboard card (UI-SPEC §9.2 player
// boards, §9.3 team boards): title + unit head, ranked rows, "Show 25"
// expand. Self-contained, props only.
//
// Row interaction:
//  - `renderAction(row)` (render prop) injects a custom action element at the
//    row end (build brief: action slot on every player/team row).
//  - `onNavigate(target)` makes the row a click target (default action:
//    profile navigation, injected by the embedding page). Without both, rows
//    are inert — no dead affordance.
//
// "Empty renders nothing": no rows -> null. No '-'/'n/a' placeholders; a
// missing subline/context simply stays absent.
import React, { useState } from "react";
import {
  CompetitionCrest,
  CompetitionFlag,
  PlayerPhoto,
} from "./shared/CompetitionMedia";
import { C, FONT_FAMILY } from "./shared/competitionsUi";
import useCompetitionsTr from "./shared/useCompetitionsTr";

const rowInteractivity = (row, onNavigate, navigable) => {
  if (!onNavigate) return {};
  const target = row.player
    ? { type: "player", id: row.player.id, row }
    : row.team
      ? { type: "team", id: row.team.id, row }
      : null;
  if (!target || target.id == null) return {};
  // Per-row affordance gate (TOTW pattern: no dead cursor on rows the page
  // cannot resolve to a profile).
  if (typeof navigable === "function" && !navigable(target)) return {};
  return {
    role: "button",
    tabIndex: 0,
    onClick: () => onNavigate(target),
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onNavigate(target);
      }
    },
    style: { cursor: "pointer" },
  };
};

const PlayerRow = ({ row, renderAction, onNavigate, navigable }) => {
  const interactive = rowInteractivity(row, onNavigate, navigable);
  const subline = [row.club && row.club.name, row.player && row.player.position]
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      {...interactive}
      style={{
        display: "grid",
        gridTemplateColumns: renderAction
          ? "30px minmax(0,1fr) 46px 54px auto"
          : "30px minmax(0,1fr) 46px 54px",
        alignItems: "center",
        gap: 12,
        padding: "0 18px",
        minHeight: 48,
        borderBottom: `1px solid ${C.rowDivider}`,
        ...(interactive.style || {}),
      }}
    >
      <span
        style={{
          fontSize: 13.5,
          color: C.ink500,
          fontVariantNumeric: "tabular-nums",
          textAlign: "right",
        }}
      >
        {row.rank}
      </span>
      <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <PlayerPhoto
          name={row.player && row.player.name}
          photo={row.player && row.player.photo}
          size={26}
        />
        <span style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              minWidth: 0,
            }}
          >
            <span
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: C.ink900,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {row.player && row.player.name}
            </span>
            <CompetitionFlag
              country={row.player && (row.player.nationality || row.player.countryCode)}
              size={11}
            />
          </span>
          {subline && (
            <span
              style={{
                fontSize: 11.5,
                color: C.ink500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {subline}
            </span>
          )}
        </span>
      </span>
      <span
        style={{
          fontSize: 12.5,
          color: C.ink400,
          fontVariantNumeric: "tabular-nums",
          textAlign: "right",
        }}
      >
        {row.context != null ? row.context : ""}
      </span>
      <span
        style={{
          fontSize: 14.5,
          fontWeight: 700,
          color: C.ink900,
          fontVariantNumeric: "tabular-nums",
          textAlign: "right",
        }}
      >
        {row.value}
      </span>
      {renderAction && (
        <span style={{ display: "inline-flex", justifyContent: "flex-end" }}>
          {renderAction(row)}
        </span>
      )}
    </div>
  );
};

const TeamRow = ({ row, renderAction, onNavigate, navigable }) => {
  const interactive = rowInteractivity(row, onNavigate, navigable);
  return (
    <div
      {...interactive}
      style={{
        display: "grid",
        gridTemplateColumns: renderAction ? "minmax(0,1fr) 64px auto" : "minmax(0,1fr) 64px",
        alignItems: "center",
        gap: 12,
        padding: "0 18px",
        minHeight: 46,
        borderBottom: `1px solid ${C.rowDivider}`,
        ...(interactive.style || {}),
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <CompetitionCrest
          name={row.team && row.team.name}
          logo={row.team && row.team.logo}
          size={24}
        />
        <span
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: C.ink900,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.team && row.team.name}
        </span>
      </span>
      <span
        style={{
          fontSize: 14.5,
          fontWeight: 700,
          color: C.ink900,
          fontVariantNumeric: "tabular-nums",
          textAlign: "right",
        }}
      >
        {row.value}
      </span>
      {renderAction && (
        <span style={{ display: "inline-flex", justifyContent: "flex-end" }}>
          {renderAction(row)}
        </span>
      )}
    </div>
  );
};

const CompetitionLeaderboardCard = ({
  title,
  unit,
  rows = [],
  kind = "player",
  visibleCount = 10,
  expandedCount = 25,
  footnote,
  renderAction,
  onNavigate,
  navigable,
}) => {
  const { tr } = useCompetitionsTr();
  const [expanded, setExpanded] = useState(false);

  // "Empty renders nothing" — a board without provider data is absent.
  if (!rows.length) return null;

  const shown = expanded ? rows.slice(0, expandedCount) : rows.slice(0, visibleCount);
  const canExpand = !expanded && rows.length > visibleCount;
  const Row = kind === "team" ? TeamRow : PlayerRow;

  return (
    <div
      style={{
        fontFamily: FONT_FAMILY,
        border: `1px solid ${C.border}`,
        borderRadius: 3,
        background: "#fff",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 10,
          padding: kind === "team" ? "13px 18px" : "14px 18px",
          borderBottom: `1px solid ${C.grid}`,
        }}
      >
        <span
          style={{
            fontSize: kind === "team" ? 14 : 14.5,
            fontWeight: 700,
            color: C.ink900,
          }}
        >
          {title}
        </span>
        {unit && (
          <span style={{ fontSize: 11.5, color: C.ink400, whiteSpace: "nowrap" }}>
            {unit}
          </span>
        )}
      </div>
      <div>
        {shown.map((row, index) => (
          <Row
            key={
              (row.player && row.player.id) ||
              (row.team && row.team.id) ||
              `${row.rank}-${index}`
            }
            row={row}
            renderAction={renderAction}
            onNavigate={onNavigate}
            navigable={navigable}
          />
        ))}
      </div>
      {(footnote || canExpand) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: footnote ? "space-between" : "flex-end",
            gap: 10,
            padding: "12px 18px",
          }}
        >
          {footnote && (
            <span style={{ fontSize: 11.5, color: C.ink400 }}>{footnote}</span>
          )}
          {canExpand && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                fontSize: 13,
                fontWeight: 600,
                color: C.accent,
                cursor: "pointer",
                fontFamily: FONT_FAMILY,
              }}
            >
              {tr("Show 25")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CompetitionLeaderboardCard;
